import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AccessLogService } from '../../../core/services/access-log.service';
import { CsvExportService } from '../../../core/services/csv-export.service';

@Component({
  selector: 'app-access-log',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './access-log.html',
  styleUrl: './access-log.scss',
})
export class AccessLogComponent {
  private readonly accessLogService = inject(AccessLogService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly snackBar = inject(MatSnackBar);

  readonly isDownloading = signal<boolean>(false);

  readonly dateRange = new FormGroup({
    start: new FormControl<Date | null>(null, [Validators.required]),
    end: new FormControl<Date | null>(null, [Validators.required]),
  });

  downloadLogs(): void {
    if (this.dateRange.invalid) {
      this.snackBar.open('期間を正しく指定してください', '閉じる', { duration: 3000 });
      return;
    }

    const { start, end } = this.dateRange.value;
    if (!start || !end) return;

    // Adjust end date to 23:59:59
    const adjustedEnd = new Date(end);
    adjustedEnd.setHours(23, 59, 59, 999);

    // Check if the range is valid (e.g. max 31 days)
    const diffTime = adjustedEnd.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 31) {
      this.snackBar.open('一度にダウンロードできるのは最大31日分です', '閉じる', {
        duration: 3000,
      });
      return;
    }

    this.isDownloading.set(true);

    this.accessLogService.fetchAccessLogs(start, adjustedEnd).subscribe({
      next: (logs) => {
        if (logs.length === 0) {
          this.snackBar.open('指定された期間のアクセスログはありません', '閉じる', {
            duration: 3000,
          });
          this.isDownloading.set(false);
          return;
        }

        try {
          const headers = this.accessLogService.getHeaders();
          const rows = this.accessLogService.mapToExportRows(logs);

          const filename = `access_logs_${this.formatDateForFilename(start)}_${this.formatDateForFilename(end)}.csv`;
          const csvContent = this.csvExportService.generateCsv(headers, rows);
          this.csvExportService.downloadCsv(csvContent, filename);

          this.snackBar.open('アクセスログのダウンロードが完了しました', '閉じる', {
            duration: 3000,
          });
        } catch (e) {
          console.error(e);
          this.snackBar.open('ダウンロード中にエラーが発生しました', '閉じる', { duration: 3000 });
        } finally {
          this.isDownloading.set(false);
        }
      },
      error: (e) => {
        console.error(e);
        this.snackBar.open('アクセスログの取得に失敗しました', '閉じる', { duration: 3000 });
        this.isDownloading.set(false);
      },
    });
  }

  private formatDateForFilename(date: Date): string {
    const yyyy = date.getFullYear();
    const MM = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}${MM}${dd}`;
  }
}
